import { WS_RECONNECT_TIMEOUT } from 'constants/webSocket'
import { captureWarning } from 'helpers/errorHelpers'
import { fromEvents } from 'helpers/observableHelpers'
import { of, Subject, merge } from 'rxjs'
import { delay, filter, finalize, map, mapTo, scan, switchMap, takeUntil, tap } from 'rxjs/operators'
import { WSEventsMap } from 'components/features/webSocket/types'


export class WebSocketConnector<IncomeEvent, OutgoingEvent> {
  private socket: WebSocket

  private disconnect$ = new Subject<null>()
  public event$ = new Subject<IncomeEvent>()
  public open$ = new Subject<null>()
  private send$ = new Subject<OutgoingEvent>()
  private sent$ = new Subject<null>()
  private reconnect$ = new Subject<null>()

  constructor (private url: string) {
    this.setupSendQueue()
    this.setupReconnect()
    this.setupSocket()
  }

  private setupSocket () {
    this.socket = new WebSocket(this.url)
    fromEvents<WSEventsMap>(this.socket, ['open', 'close', 'error', 'message']).pipe(
      takeUntil(this.disconnect$),
      takeUntil(this.reconnect$),
      finalize(() => {
        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
          this.socket.close()
        }
      }),
      tap((event) => {
        switch (event.type) {
          case 'close':
          case 'error': {
            this.reconnect$.next(null)
            break
          }

          case 'open': {
            this.open$.next(null)
            break
          }

          case 'message': {
            try {
              const message: IncomeEvent = JSON.parse(event.data)
              this.event$.next(message)
            } catch (e) {
              captureWarning('Cannot parse ws message', { data: event.data })
            }

          }

        }
      }),
    ).subscribe()
  }

  private setupSendQueue () {
    merge(
      this.send$.pipe(
        map((payload) => ({
          type: 'send' as const,
          payload,
        }))
      ),
      this.sent$.pipe(mapTo({ type: 'sent' as const })),
      this.open$.pipe(mapTo({ type: 'open' as const })),
      this.disconnect$.pipe(mapTo({ type: 'disconnect' as const })),
      this.reconnect$.pipe(mapTo({ type: 'reconnect' as const })),
    ).pipe(
      takeUntil(this.disconnect$),
      scan((socketState, event) => {
        switch (event.type) {
          case 'sent': {
            return {
              ...socketState,
              queue: [],
            }
          }

          case 'send': {
            return {
              ...socketState,
              queue: [
                ...socketState.queue,
                event.payload,
              ],
            }
          }


          case 'open': {
            return {
              ...socketState,
              isOpen: true,
            }
          }

          case 'disconnect': {
            return {
              queue: [],
              isOpen: false,
            }
          }

          case 'reconnect': {
            return {
              ...socketState,
              isOpen: false,
            }
          }

          default: {
            return socketState
          }
        }
      }, {
        isOpen: false,
        queue: [] as OutgoingEvent[],
      }),
      filter(({ isOpen, queue }) => isOpen && queue.length !== 0),
      tap(({ queue }) => {
        const socket = this.socket
        if (!socket) {
          captureWarning('Socket not defined')
          return
        }

        queue.forEach((message) => {
          try {
            socket.send(JSON.stringify(message, (_, value) => {
              if (value?.streamId) {
                return `streamId=${ value.streamId }`
              }
              return value
            }))
          } catch (e) {
            try {
              captureWarning('WebSocketConnector can\'t serialize on send', message)
            } catch (ee) {}
          }
        })
        this.sent$.next(null)
      }),
    ).subscribe()
  }

  private setupReconnect () {
    this.reconnect$.pipe(
      takeUntil(this.disconnect$),
      switchMap(() => of(null).pipe(
        takeUntil(this.disconnect$),
        delay(WS_RECONNECT_TIMEOUT),
        tap(() => this.setupSocket())
      ))
    ).subscribe()
  }

  disconnect () {
    this.disconnect$.next(null)
  }

  send (event: OutgoingEvent) {
    this.send$.next(event)
  }
}
